import {
  clearRange,
  copyRange,
  deleteColumns,
  deleteRows,
  inRange,
  insertColumn,
  insertRow,
  normalize,
  paste,
  type Range,
  setCell,
  type Table,
} from '@/app/sheet/edit';
import {
  columnName,
  display,
  evaluate,
} from '@/app/sheet/sheet';
import classNames from 'classnames';
import {
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PopupMenu } from '../PopupMenu';

type Pos = { r: number, c: number };

const same = (a: Pos | null, b: Pos) => a?.r === b.r && a.c === b.c;

const rangeOf = (a: Pos, b: Pos): Range => ({
  top: Math.min(a.r, b.r),
  left: Math.min(a.c, b.c),
  bottom: Math.max(a.r, b.r),
  right: Math.max(a.c, b.c),
});

// how long a touch is held to open the menu, and how far it may wander
const LONG_PRESS_MS = 500;
const LONG_PRESS_SLOP_PX = 10;

// The cell editor grows out of its cell over the neighbours, but the grid's
// scroll box clips it: it grows toward whichever side has the room for it,
// and no further than that box's visible edge, scrolling its text beyond.
const fitEditor = (input: HTMLInputElement) => {
  const cell = input.parentElement!.getBoundingClientRect();
  const box = input.closest('.sheet-scroll')!.getBoundingClientRect();
  const right = box.right - cell.left;
  const left = cell.right - box.left;
  const widest = parseFloat(getComputedStyle(input).fontSize) * 40;
  const growLeft = right < widest && left > right;
  input.classList.toggle('grow-left', growLeft);
  input.style.setProperty('--room', `${growLeft ? left : right}px`);
};

// What a grid last put on the clipboard, and from where, so pasting it back
// can move the references it takes along. A cut moves references from
// anywhere in its own grid too, on its first paste there only.
let copied: { text: string, range: Range, grid: symbol, cut: boolean } | null =
  null;

// A ```sheet block as a grid, inside the note's editor. Keys go as in a
// spreadsheet: arrows and Tab move, Enter or typing edits, Enter again
// commits and moves down - adding a row at the bottom - and Escape cancels.
// Shift with an arrow or a click, or a drag, selects a block of cells from
// the anchor to the active one; delete, copy, cut and paste take the block.
// A right click, or a long press on touch, opens a menu to add and remove
// rows and columns around the cell or the selected block.
// Every change goes out through onChange as a whole new table.
export const SheetGrid = (
  { table, onChange, onUndo, onRedo, onEditText }: {
    table: Table,
    onChange: (table: Table) => void,
    onUndo: () => void,
    onRedo: () => void,
    onEditText: () => void,
  },
) => {
  const rows = useMemo(() => normalize(table), [table]);
  const cells = useMemo(() => evaluate(rows), [rows]);
  const [active, setActive] = useState<Pos | null>(null);
  // the selection's other corner; none selects just the active cell
  const [anchor, setAnchor] = useState<Pos | null>(null);
  // where a drag started, while the button is down
  const dragFrom = useRef<Pos | null>(null);
  const [draft, setDraft] = useState<string | null>(null);
  // the edit in progress, readable by the blur that follows a commit
  const editing = useRef<{ at: Pos, text: string } | null>(null);
  const grid = useRef<HTMLDivElement>(null);
  const [id] = useState(() => Symbol('grid'));
  // the menu, where it opened and the block its edits act on
  const [menu, setMenu] = useState<
    { top: number, left: number, on: Range } | null
  >(null);
  // a touch held down, until it's long enough to open the menu
  const press = useRef<
    { timer: number, x: number, y: number } | null
  >(null);

  // a COUNTBY block can reach past the table, and the grid shows it all
  const height = cells.length;
  const width = cells[0].length;
  const rawAt = ({ r, c }: Pos) => rows[r]?.[c] ?? '';
  const clamp = ({ r, c }: Pos): Pos => ({
    r: Math.max(0, Math.min(height - 1, r)),
    c: Math.max(0, Math.min(width - 1, c)),
  });

  const range = active ? rangeOf(anchor ?? active, active) : null;
  const block = !!anchor && !!active && !same(anchor, active);

  const select = (at: Pos, from: Pos | null = null) => {
    setAnchor(from);
    setActive(at);
  };

  const change = (next: Table) => {
    if (next !== rows) onChange(next);
  };

  useEffect(() => {
    const stop = () => {
      dragFrom.current = null;
    };
    window.addEventListener('mouseup', stop);
    return () => window.removeEventListener('mouseup', stop);
  }, []);

  const startEdit = (at: Pos, text: string) => {
    editing.current = { at, text };
    select(at);
    setDraft(text);
  };

  // writes the edit, if any, and moves by dr/dc; moving down off the last
  // row adds one. Returns the table as it now is.
  const commit = (dr = 0, dc = 0, refocus = true): Table => {
    const edit = editing.current;
    editing.current = null;
    setDraft(null);
    const at = edit?.at ?? active;
    if (!at) return rows;
    let next = edit && edit.text !== rawAt(at)
      ? setCell(rows, at.r, at.c, edit.text)
      : rows;
    const to = { r: at.r + dr, c: at.c + dc };
    const bottom = Math.max(next.length, height);
    if (dr > 0 && to.r >= bottom) next = insertRow(next, next.length);
    if (next !== rows) onChange(next);
    setActive({
      r: Math.max(0, Math.min(Math.max(next.length, height) - 1, to.r)),
      c: Math.max(0, Math.min(width - 1, to.c)),
    });
    if (refocus) grid.current?.focus({ preventScroll: true });
    return next;
  };

  const cancel = () => {
    editing.current = null;
    setDraft(null);
    grid.current?.focus({ preventScroll: true });
  };

  const onGridKey = (e: KeyboardEvent) => {
    const mod = e.metaKey || e.ctrlKey;
    if (mod && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      return e.shiftKey ? onRedo() : onUndo();
    }
    if (mod && e.key.toLowerCase() === 'y') {
      e.preventDefault();
      return onRedo();
    }
    if (!active) return;
    if (mod && e.key.toLowerCase() === 'a') {
      e.preventDefault();
      return select({ r: height - 1, c: width - 1 }, { r: 0, c: 0 });
    }
    const moves: Record<string, [number, number]> = {
      ArrowUp: [-1, 0],
      ArrowDown: [1, 0],
      ArrowLeft: [0, -1],
      ArrowRight: [0, 1],
      Tab: [0, e.shiftKey ? -1 : 1],
    };
    const move = moves[e.key];
    if (move) {
      e.preventDefault();
      const to = clamp({ r: active.r + move[0], c: active.c + move[1] });
      select(to, e.shiftKey && e.key !== 'Tab' ? anchor ?? active : null);
    } else if (e.key === 'Enter' || e.key === 'F2') {
      e.preventDefault();
      startEdit(active, rawAt(active));
    } else if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      change(clearRange(rows, range!));
    } else if (e.key === 'Escape') {
      if (block) setAnchor(null);
      else setActive(null);
    } else if (e.key.length === 1 && !mod && !e.altKey) {
      e.preventDefault();
      startEdit(active, e.key);
    }
  };

  const onInputKey = (e: KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      e.preventDefault();
      commit(e.shiftKey ? -1 : 1, 0);
    } else if (e.key === 'Tab') {
      e.preventDefault();
      commit(0, e.shiftKey ? -1 : 1);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  };

  // a click picks a cell, a second click on it edits - the same on touch.
  // With shift it selects the block from the anchor; held, it drags one out.
  // Any other button leaves a selected block alone, for the menu to take.
  const onCellDown = (e: MouseEvent, at: Pos) => {
    if (draft !== null && same(editing.current?.at ?? null, at)) return;
    e.preventDefault();
    if (draft !== null) commit();
    if (e.button !== 0) {
      if (!range || !inRange(range, { row: at.r, col: at.c })) select(at);
      return grid.current?.focus({ preventScroll: true });
    }
    if (e.shiftKey && active) select(at, anchor ?? active);
    else if (same(active, at) && !block && draft === null) {
      return startEdit(at, rawAt(at));
    } else {
      select(at);
      dragFrom.current = at;
    }
    grid.current?.focus({ preventScroll: true });
  };

  const onCellEnter = (at: Pos) => {
    if (dragFrom.current) select(at, dragFrom.current);
  };

  // opened on a cell in the selected block it acts on the block, anywhere
  // else on that cell alone, which it selects
  const openMenu = (at: Pos, x: number, y: number) => {
    if (draft !== null) commit(0, 0, false);
    const inside = range && inRange(range, { row: at.r, col: at.c });
    if (!inside) select(at);
    setMenu({ top: y, left: x, on: inside ? range : rangeOf(at, at) });
  };

  // an edit leaves the top left cell of what it acted on selected, with the
  // grid focused again
  const act = (edit: (table: Table, on: Range) => Table) => () => {
    if (!menu) return;
    setMenu(null);
    select({ r: menu.on.top, c: menu.on.left });
    grid.current?.focus({ preventScroll: true });
    change(edit(rows, menu.on));
  };

  const closeMenu = (refocus: boolean) => {
    setMenu(null);
    if (refocus) grid.current?.focus({ preventScroll: true });
    else {
      setAnchor(null);
      setActive(null);
    }
  };

  const cancelPress = () => {
    if (press.current) clearTimeout(press.current.timer);
    press.current = null;
  };

  // a phone's own long press opens a context menu on some systems and not
  // others, so a held touch opens ours either way
  const onCellPointerDown = (e: PointerEvent, at: Pos) => {
    cancelPress();
    if (e.pointerType !== 'touch') return;
    const { clientX: x, clientY: y } = e;
    press.current = {
      x,
      y,
      timer: window.setTimeout(() => {
        press.current = null;
        openMenu(at, x, y);
      }, LONG_PRESS_MS),
    };
  };

  const onCellPointerMove = (e: PointerEvent) => {
    const p = press.current;
    if (
      p && Math.hypot(e.clientX - p.x, e.clientY - p.y) > LONG_PRESS_SLOP_PX
    ) cancelPress();
  };

  useEffect(() => cancelPress, []);

  const many = (on: Range, axis: 'row' | 'col') =>
    axis === 'row' ? on.bottom > on.top : on.right > on.left;

  // the menu sits beside the grid, not in it: events bubble out of a portal
  // along the component tree, and the grid's keys and blur aren't the menu's
  return (
    <>
      <div
        className="sheet-grid"
        ref={grid}
        tabIndex={0}
        onKeyDown={onGridKey}
        onBlur={e => {
          if (grid.current?.contains(e.relatedTarget)) return;
          // the menu takes focus, and closing it says what becomes of it
          if (menu || e.relatedTarget?.closest('.popup-menu')) return;
          if (editing.current) commit(0, 0, false);
          setAnchor(null);
          setActive(null);
        }}
        onPaste={e => {
          if (draft !== null || !range) return;
          e.preventDefault();
          const text = e.clipboardData.getData('text');
          const ours = copied?.text === text ? copied : null;
          const cut = !!ours?.cut && ours.grid === id;
          if (cut) copied = { ...ours, cut: false };
          change(
            paste(
              rows,
              range,
              text,
              ours ? { range: ours.range, cut } : undefined,
            ),
          );
        }}
        onCopy={e => {
          if (draft !== null || !range) return;
          e.preventDefault();
          const text = copyRange(cells, range);
          e.clipboardData.setData('text/plain', text);
          copied = { text, range, grid: id, cut: false };
        }}
        onCut={e => {
          if (draft !== null || !range) return;
          e.preventDefault();
          const text = copyRange(cells, range);
          e.clipboardData.setData('text/plain', text);
          copied = { text, range, grid: id, cut: true };
          change(clearRange(rows, range));
        }}>
        <div className="sheet-scroll">
          <table>
            <thead>
              <tr>
                <th className="corner" />
                {cells[0].map((_, c) => (
                  <th
                    key={c}
                    className={classNames({
                      current: range && c >= range.left && c <= range.right,
                    })}>
                    {columnName(c)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cells.map((row, r) => (
                <tr key={r} className={classNames({ header: r === 0 })}>
                  <th
                    className={classNames({
                      current: range && r >= range.top && r <= range.bottom,
                    })}>
                    {r + 1}
                  </th>
                  {row.map((cell, c) => {
                    const at = { r, c };
                    const isEditing = draft !== null && same(active, at);
                    return (
                      <td
                        key={c}
                        onMouseDown={e => onCellDown(e, at)}
                        onPointerDown={e => onCellPointerDown(e, at)}
                        onPointerMove={onCellPointerMove}
                        onPointerUp={cancelPress}
                        onPointerCancel={cancelPress}
                        onContextMenu={e => {
                          e.preventDefault();
                          cancelPress();
                          openMenu(at, e.clientX, e.clientY);
                        }}
                        onMouseEnter={() => onCellEnter(at)}
                        className={classNames({
                          active: same(active, at),
                          selected: block
                            && inRange(range!, { row: r, col: c }),
                          number: typeof cell.value === 'number',
                          formula: cell.raw.startsWith('='),
                          error: cell.error,
                          spilled: !!cell.spilled,
                        })}>
                        {isEditing
                          ? (
                            <input
                              ref={el => {
                                if (!el) return;
                                fitEditor(el);
                                el.focus({ preventScroll: true });
                              }}
                              value={draft}
                              style={{
                                '--chars': draft.length,
                              } as CSSProperties}
                              onChange={e => {
                                editing.current = {
                                  at,
                                  text: e.target.value,
                                };
                                setDraft(e.target.value);
                              }}
                              onKeyDown={onInputKey}
                              onBlur={() => {
                                if (editing.current) commit(0, 0, false);
                              }} />
                          )
                          : display(cell.value)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {menu && (
        <PopupMenu
          at={{ top: menu.top, left: menu.left }}
          onClose={closeMenu}>
          <button
            role="menuitem"
            onClick={act((t, on) => insertRow(t, on.top))}>
            Insert row above
          </button>
          <button
            role="menuitem"
            onClick={act((t, on) => insertRow(t, on.bottom + 1))}>
            Insert row below
          </button>
          <button
            role="menuitem"
            onClick={act((t, on) => insertColumn(t, on.left))}>
            Insert column left
          </button>
          <button
            role="menuitem"
            onClick={act((t, on) => insertColumn(t, on.right + 1))}>
            Insert column right
          </button>
          <hr />
          <button
            role="menuitem"
            className="danger"
            onClick={act((t, on) => deleteRows(t, on.top, on.bottom))}>
            {many(menu.on, 'row') ? 'Delete rows' : 'Delete row'}
          </button>
          <button
            role="menuitem"
            className="danger"
            onClick={act((t, on) => deleteColumns(t, on.left, on.right))}>
            {many(menu.on, 'col') ? 'Delete columns' : 'Delete column'}
          </button>
          <hr />
          <button
            role="menuitem"
            onClick={() => {
              setMenu(null);
              onEditText();
            }}>
            Edit as text
          </button>
        </PopupMenu>
      )}
    </>
  );
};
